"use client";

import React from "react";
import moment, { Moment } from "moment";
import {
  programsStudentAcademy,
  studentProgramAssistancesAcademy,
} from "../../../../shared/services";
import { AttendanceStatus } from "../../../../types/dto/program";

export interface AttendanceDailyRow {
  programStudentId: number;
  userId: number;
  userName: string;
  studentCode?: string;
  recordId?: number;
  attendanceStatus: AttendanceStatus | null;
  dirty: boolean;
}

export default function useProgramAttendanceDaily(
  programId: number,
  selectedDate: Moment
) {
  const [loading, setLoading] = React.useState(false);
  const [rows, setRows] = React.useState<AttendanceDailyRow[]>([]);

  const load = React.useCallback(async () => {
    if (!programId) return;
    setLoading(true);
    try {
      const dateStr = selectedDate.format("YYYY-MM-DD");
      const [studentsRes, assistancesRes] = await Promise.all([
        programsStudentAcademy.find({
          query: {
            program_id: programId,
            status: "active",
            $limit: 10000,
            $sort: { id: -1 },
          },
        }),
        studentProgramAssistancesAcademy.find({
          query: { program_id: programId, $limit: 10000 },
        }),
      ]);

      const students = Array.isArray(studentsRes)
        ? studentsRes
        : studentsRes.data ?? [];

      const assistances = Array.isArray(assistancesRes)
        ? assistancesRes
        : assistancesRes.data ?? [];

      const assistancesByStudent = new Map<number, any>();
      for (const record of assistances) {
        const recordDate = moment(record.assistance_date).format("YYYY-MM-DD");
        if (recordDate === dateStr) {
          assistancesByStudent.set(record.program_student_id, record);
        }
      }

      const mappedRows: AttendanceDailyRow[] = students.map(
        (student: any) => {
          const attendance = assistancesByStudent.get(student.id);
          const userName = `${student.user?.first_name ?? ""} ${
            student.user?.last_name ?? ""
          }`.trim();

          return {
            programStudentId: student.id,
            userId: student.user_id,
            userName: userName || `Estudiante #${student.user_id}`,
            studentCode: student.user?.student_code,
            recordId: attendance?.id,
            attendanceStatus: attendance?.attendance_status ?? null,
            dirty: false,
          };
        }
      );

      const nextRows = mappedRows.sort((a, b) =>
        a.userName.localeCompare(b.userName, "es")
      );

      setRows(nextRows);
    } catch (err) {
      console.error("Error al cargar asistencia del día:", err);
    } finally {
      setLoading(false);
    }
  }, [programId, selectedDate]);

  React.useEffect(() => {
    load();
  }, [load]);

  const setRowStatus = (
    programStudentId: number,
    status: AttendanceStatus
  ) => {
    setRows((prev) =>
      prev.map((row) =>
        row.programStudentId === programStudentId
          ? { ...row, attendanceStatus: status, dirty: true }
          : row
      )
    );
  };

  const dirtyRows = rows.filter((row) => row.dirty);
  const hasChanges = dirtyRows.length > 0;

  return {
    loading,
    rows,
    setRowStatus,
    load,
    dirtyRows,
    hasChanges,
  };
}
